import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function PortfolioPage() {
  const [scrolled, setScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const portfolioItems = [
    {
      number: '01',
      name: 'Diitect',
      tag: 'Patient Safety',
      icon: 'ri-shield-check-line',
      description: 'Fall detection and passive monitoring technology for care environments where speed, awareness, and reliability are critical.',
      image: 'https://public.readdy.ai/ai/img_res/542dc293ac923d09a8db697d1eda751f.jpg',
    },
    {
      number: '02',
      name: 'Vetuvia',
      tag: 'Veterinary Infrastructure',
      icon: 'ri-heart-pulse-line',
      description: 'A platform supporting modern veterinary care delivery and commerce, designed for flexibility, access, and scalable economics.',
      image: 'https://readdy.ai/api/search-image?query=premium%20modern%20veterinary%20clinic%20interior%20with%20clean%20white%20surfaces%20and%20warm%20wood%20accents%2C%20minimalist%20professional%20environment%20with%20soft%20natural%20light%2C%20sophisticated%20care%20facility%20with%20contemporary%20design%20elements%2C%20editorial%20architectural%20photography%20with%20warm%20neutral%20tones%2C%20no%20people%2C%20calm%20and%20refined%20atmosphere&width=800&height=500&seq=portfolio-vetuvia-v3&orientation=landscape',
    },
    {
      number: '03',
      name: 'RFID Solutions',
      tag: 'Industrial Tracking',
      icon: 'ri-radar-line',
      description: 'RFID-based systems serving medical and tire industry workflows where asset visibility, traceability, and process efficiency are essential.',
      image: 'https://readdy.ai/api/search-image?query=sleek%20industrial%20facility%20with%20precision%20manufacturing%20equipment%20and%20clean%20organized%20workflow%2C%20modern%20logistics%20environment%20with%20warm%20metallic%20tones%20and%20structured%20lighting%2C%20sophisticated%20operational%20space%20with%20geometric%20precision%2C%20editorial%20photography%20with%20cinematic%20depth%2C%20no%20people%2C%20professional%20industrial%20aesthetic&width=800&height=500&seq=portfolio-rfid-v3&orientation=landscape',
    },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Portfolio',
        item: `${siteUrl}/portfolio`
      }
    ]
  };

  const portfolioSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Diivy Portfolio Companies',
    description: 'A focused group of operating initiatives across safety, care infrastructure, and industrial visibility systems.',
    url: `${siteUrl}/portfolio`,
    numberOfItems: 3,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Organization',
          name: 'Diitect',
          description: 'Fall detection and passive monitoring technology for care environments where speed, awareness, and reliability are critical.',
          url: `${siteUrl}/portfolio`,
          category: 'Patient Safety'
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Organization',
          name: 'Vetuvia',
          description: 'A platform supporting modern veterinary care delivery and commerce, designed for flexibility, access, and scalable economics.',
          url: `${siteUrl}/portfolio`,
          category: 'Veterinary Infrastructure'
        }
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Organization',
          name: 'RFID Solutions',
          description: 'RFID-based systems serving medical and tire industry workflows where asset visibility, traceability, and process efficiency are essential.',
          url: `${siteUrl}/portfolio`,
          category: 'Industrial Tracking'
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Portfolio | Diivy Companies - Diitect, Vetuvia & RFID Solutions"
        description="Explore Diivy's portfolio of focused companies: Diitect (medical fall detection technology), Vetuvia (veterinary health platform), and RFID Solutions (tire tracking for FIFA World Cup). Each built around meaningful problems and long-term potential."
        keywords="Diivy portfolio, Diitect, Vetuvia, RFID solutions, medical technology, veterinary platform, fall detection, tire tracking, FIFA World Cup"
        canonical="/portfolio"
        ogTitle="Portfolio | Diivy Companies - Diitect, Vetuvia & RFID Solutions"
        ogDescription="Explore Diivy's portfolio: Diitect (medical fall detection), Vetuvia (veterinary health platform), and RFID Solutions (tire tracking)."
        ogUrl="/portfolio"
        twitterTitle="Portfolio | Diivy Companies - Diitect, Vetuvia & RFID Solutions"
        twitterDescription="Explore Diivy's portfolio: Diitect, Vetuvia, and RFID Solutions built for long-term impact."
      />
      <JsonLd data={portfolioSchema} />
      <JsonLd data={breadcrumbSchema} />
      
      <div className="min-h-screen bg-[#F7F4EF]">
        <Navigation />

        {/* Hero */}
        <section className="pt-40 pb-28 bg-white">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Portfolio
              </p>
            </div>

            <h1 className="text-[56px] lg:text-[68px] font-light text-[#0A0F1E] mb-10 leading-[1.1] tracking-[-0.025em]">
              Operating <span className="font-semibold">Ventures</span>
            </h1>

            <p className="text-[19px] text-gray-600 leading-[1.7] max-w-[720px] font-light">
              A focused group of operating initiatives across safety, care infrastructure, and industrial visibility systems.
            </p>
          </div>
        </section>

        {/* Portfolio Entries — Premium List Style */}
        <section className="bg-[#0A0F1E] py-32">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="space-y-0">
              {portfolioItems.map((item, index) => (
                <div
                  key={index}
                  className="group grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-white/8 py-20 hover:border-[#C9A96E]/50 transition-all duration-700 cursor-default"
                >
                  {/* Number */}
                  <div className="lg:col-span-1 flex items-start pt-2">
                    <span className="text-[11px] font-semibold tracking-[0.2em] text-white/20 group-hover:text-[#C9A96E] transition-colors duration-500">
                      {item.number}
                    </span>
                  </div>

                  {/* Name + Tag + Icon */}
                  <div className="lg:col-span-3 space-y-5">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 flex items-center justify-center border border-white/12 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-500">
                        <i className={`${item.icon} text-[17px] text-white/35 group-hover:text-[#0A0F1E] transition-colors duration-500`}></i>
                      </div>
                    </div>
                    <h3 className="text-[36px] font-light text-white group-hover:text-[#C9A96E] transition-colors duration-500 tracking-[-0.015em] leading-[1.2]">
                      {item.name}
                    </h3>
                    <span className="inline-block text-[10px] font-semibold tracking-[0.2em] uppercase text-white/25 border border-white/12 group-hover:border-[#C9A96E]/50 group-hover:text-[#C9A96E]/80 px-4 py-1.5 transition-all duration-500">
                      {item.tag}
                    </span>
                  </div>

                  {/* Description */}
                  <div className="lg:col-span-5 flex items-center">
                    <p className="text-[16px] text-white/45 leading-[1.85] group-hover:text-white/65 transition-colors duration-500 max-w-[520px] font-light">
                      {item.description}
                    </p>
                  </div>

                  {/* Image + Arrow */}
                  <div className="lg:col-span-3 flex items-center justify-end gap-6">
                    <div className="w-40 h-24 overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-700 translate-x-6 group-hover:translate-x-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="w-11 h-11 flex items-center justify-center border border-white/15 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-500">
                      <i className="ri-arrow-right-line text-white/30 group-hover:text-[#0A0F1E] text-[15px] transition-colors duration-500"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Selected Project Experience */}
        <section className="py-36 bg-[#F7F4EF]">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="flex items-end justify-between border-b border-[#0A0F1E]/8 pb-14 mb-20">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                  <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                    Project Experience
                  </p>
                </div>
                <h2 className="text-[44px] font-light text-[#0A0F1E] tracking-[-0.02em] leading-[1.2]">
                  Selected <span className="font-semibold">Experience</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-[#0A0F1E]/8 py-16 group">
              <div className="lg:col-span-1 flex items-start pt-2">
                <span className="text-[11px] font-semibold tracking-[0.2em] text-[#C9A96E]">01</span>
              </div>
              <div className="lg:col-span-4 space-y-4">
                <h3 className="text-[28px] font-light text-[#0A0F1E] tracking-[-0.015em] leading-[1.3]">FIFA World Cup Qatar</h3>
                <span className="inline-block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0A0F1E]/25 border border-[#0A0F1E]/12 px-4 py-1.5">
                  2022
                </span>
              </div>
              <div className="lg:col-span-7 flex items-center">
                <p className="text-[16px] text-gray-600 leading-[1.85] max-w-[560px] font-light">
                  Participation in project work connected to the 2022 FIFA World Cup Qatar, reflecting experience in large-scale, high-visibility environments.
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}